import React from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import SearchIcon from '@material-ui/icons/Search';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import HorizontalSpacer from '../../shared/components/HorizontalSpacer/HorizontalSpacer';
import NoDataFound from '../../shared/components/NoDataFound/NoDataFound';
import ZipcodeInsightsRepository from '../../repository/zipcodeInsights/ZipcodeInsightsRepository';
import styles from './ZipcodeInsights.module.css';

const COUNTRIES = ['BE', 'FR', 'NL', 'DE'];

const LANGUAGES = [
  { code: 'nl', label: 'NL' },
  { code: 'fr', label: 'FR' },
];

const PERIODS = [6, 9, 12];

const CATEGORY_COLORS = [
  '#1976d2',
  '#ff9100',
  '#388e3c',
  '#d32f2f',
  '#7b1fa2',
  '#00796b',
  '#fbc02d',
  '#5d4037',
  '#455a64',
  '#c2185b',
  '#8bc34a',
];

const today = () => moment().format('YYYY-MM-DD');

const parseZipcodes = (value) =>
  (value || '')
    .split(/[,\s;]+/)
    .map((zipcode) => zipcode.trim())
    .filter((zipcode) => zipcode.length)
    .filter((zipcode, index, all) => all.indexOf(zipcode) === index);

const formatNumber = (value) => Number(value || 0).toLocaleString();

const getTotalCategoryScans = (categories) =>
  categories.reduce((total, category) => total + category.scanCount, 0);

const getCategoryPercentage = (category, total) =>
  total ? Math.round((category.scanCount / total) * 100) : 0;

const buildPieBackground = (categories) => {
  const total = getTotalCategoryScans(categories);
  if (!total) {
    return '#eeeeee';
  }

  let current = 0;
  const stops = categories.map((category, index) => {
    const start = current;
    const end = current + (category.scanCount / total) * 100;
    current = end;
    const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
    return `${color} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
  });

  return `conic-gradient(${stops.join(', ')})`;
};

export default function ZipcodeInsights() {
  const [countryCode, setCountryCode] = React.useState('BE');
  const [language, setLanguage] = React.useState('nl');
  const [referenceDate, setReferenceDate] = React.useState(today());
  const [periodMonths, setPeriodMonths] = React.useState(6);
  const [zipcodesInput, setZipcodesInput] = React.useState('');
  const [radiusKm, setRadiusKm] = React.useState(5);
  const [isBusy, setIsBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const zipcodes = parseZipcodes(zipcodesInput);
  const multipleZipcodes = zipcodes.length > 1;

  const validate = () => {
    if (!countryCode) {
      toastr.warning('Invalid filters', 'Country is required');
      return false;
    }

    if (!language) {
      toastr.warning('Invalid filters', 'Language is required');
      return false;
    }

    if (!zipcodes.length) {
      toastr.warning('Invalid filters', 'At least one ZIP code is required');
      return false;
    }

    if (!referenceDate || referenceDate > today()) {
      toastr.warning(
        'Invalid filters',
        'Reference date cannot be in the future',
      );
      return false;
    }

    const parsedRadius = Number(radiusKm);
    if (!multipleZipcodes && (parsedRadius < 1 || parsedRadius > 100)) {
      toastr.warning('Invalid filters', 'Radius must be between 1 and 100 km');
      return false;
    }

    return true;
  };

  const buildRequest = () => ({
    countryCode,
    language,
    referenceDate,
    periodMonths: Number(periodMonths),
    zipcodes,
    radiusKm: multipleZipcodes ? null : Number(radiusKm),
  });

  const search = (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setIsBusy(true);
    ZipcodeInsightsRepository.getInsights(buildRequest())
      .then((data) => {
        setResult(data);
      })
      .catch((error) => {
        toastr.error('Could not load zipcode insights', error.message);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const renderSummary = () => {
    if (!result) {
      return null;
    }

    return (
      <>
        <HorizontalSpacer />
        <div className={styles.summaryGrid}>
          <Paper className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Total scans</p>
            <p className={styles.summaryValue}>
              {formatNumber(result.totalScans)}
            </p>
          </Paper>
          <Paper className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Shops in area</p>
            <p className={styles.summaryValue}>
              {formatNumber(result.area.shopCount)}
            </p>
          </Paper>
          <Paper className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Resolved ZIPs</p>
            <p className={styles.summaryValue}>
              {formatNumber(result.area.resolvedZipcodes.length)}
            </p>
          </Paper>
          <Paper className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Period</p>
            <p className={styles.summaryValue}>{result.period.months}m</p>
            <Typography variant="caption">
              {result.period.from} - {result.period.to}
            </Typography>
          </Paper>
        </div>
      </>
    );
  };

  const renderCategoryLegend = (categories) => {
    const total = getTotalCategoryScans(categories);

    return (
      <div className={styles.legend}>
        {categories.map((category, index) => (
          <div
            className={styles.legendItem}
            key={category.categoryId || 'other'}>
            <span
              className={styles.legendSwatch}
              style={{
                backgroundColor:
                  CATEGORY_COLORS[index % CATEGORY_COLORS.length],
              }}
            />
            <span className={styles.legendLabel}>{category.categoryName}</span>
            <span>{getCategoryPercentage(category, total)}%</span>
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryTable = (categories) => (
    <Paper className={styles.panel}>
      <Typography variant="h6">Categories</Typography>
      <div className={styles.tableWrapper}>
        <Table size="small" id="zipcodeInsightsCategoriesTable">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="right">Scans</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.categoryId || 'other'}>
                <TableCell>{category.categoryName}</TableCell>
                <TableCell align="right">
                  {formatNumber(category.scanCount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );

  const renderTopCities = (cities) => (
    <Paper className={styles.panel}>
      <Typography variant="h6">
        Top active cities for selected country/language/period
      </Typography>
      <Typography className={styles.panelHint} variant="body2">
        This ranking does not take ZIP code or radius into account.
      </Typography>
      <div className={styles.tableWrapper}>
        <Table size="small" id="zipcodeInsightsTopCitiesTable">
          <TableHead>
            <TableRow>
              <TableCell>City</TableCell>
              <TableCell align="right">Scans</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cities.map((city) => (
              <TableRow key={city.city}>
                <TableCell>{city.city}</TableCell>
                <TableCell align="right">
                  {formatNumber(city.scanCount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );

  const renderResults = () => {
    if (!result) {
      return <NoDataFound />;
    }

    const categories = result.categoryBreakdown || [];
    const topCities = result.topCities || [];

    return (
      <>
        {renderSummary()}
        <HorizontalSpacer />
        {categories.length ? (
          <div className={styles.contentGrid}>
            <Paper className={styles.panel}>
              <Typography variant="h6">Category split</Typography>
              <div
                className={styles.pieChart}
                style={{ background: buildPieBackground(categories) }}
                aria-label="Category split pie chart"
              />
              {renderCategoryLegend(categories)}
            </Paper>
            {renderCategoryTable(categories)}
          </div>
        ) : (
          <NoDataFound />
        )}
        <HorizontalSpacer />
        {topCities.length ? renderTopCities(topCities) : <NoDataFound />}
        {result.disclaimers && result.disclaimers.length ? (
          <>
            <HorizontalSpacer />
            <div className={styles.disclaimers}>
              {result.disclaimers.map((disclaimer) => (
                <Alert severity="info" key={disclaimer}>
                  {disclaimer}
                </Alert>
              ))}
            </div>
          </>
        ) : null}
      </>
    );
  };

  return (
    <div className={styles.zipcodeInsights}>
      <h1>Zipcode Insights</h1>
      <Paper className={styles.filters}>
        <form id="zipcodeInsightsForm" onSubmit={search} autoComplete="off">
          <div className={styles.filterGrid}>
            <FormControl margin="normal" required>
              <InputLabel shrink htmlFor="zipcodeInsightsCountry">
                Country
              </InputLabel>
              <Select
                id="zipcodeInsightsCountry"
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}>
                {COUNTRIES.map((countryCode) => (
                  <MenuItem key={countryCode} value={countryCode}>
                    {countryCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl margin="normal" required>
              <InputLabel shrink htmlFor="zipcodeInsightsLanguage">
                Language
              </InputLabel>
              <Select
                id="zipcodeInsightsLanguage"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}>
                {LANGUAGES.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              id="zipcodeInsightsReferenceDate"
              label="Reference date"
              type="date"
              value={referenceDate}
              onChange={(event) => setReferenceDate(event.target.value)}
              inputProps={{ max: today() }}
              margin="normal"
              required
            />

            <FormControl margin="normal" required>
              <InputLabel shrink htmlFor="zipcodeInsightsPeriod">
                Period
              </InputLabel>
              <Select
                id="zipcodeInsightsPeriod"
                value={periodMonths}
                onChange={(event) => setPeriodMonths(event.target.value)}>
                {PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period} months
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              id="zipcodeInsightsZipcodes"
              className={styles.zipcodesField}
              label="ZIP codes"
              value={zipcodesInput}
              onChange={(event) => setZipcodesInput(event.target.value)}
              margin="normal"
              required
            />

            <TextField
              id="zipcodeInsightsRadius"
              label="Radius km"
              type="number"
              value={radiusKm}
              onChange={(event) => setRadiusKm(event.target.value)}
              inputProps={{ min: 1, max: 100 }}
              margin="normal"
              disabled={multipleZipcodes}
              required={!multipleZipcodes}
            />

            <div className={styles.actions}>
              <Button
                id="zipcodeInsightsSearchButton"
                color="primary"
                variant="contained"
                type="submit"
                disabled={isBusy}>
                <SearchIcon />
                Search
              </Button>
            </div>
          </div>
        </form>
      </Paper>

      <HorizontalSpacer />
      {isBusy ? (
        <CircularProgress id="zipcodeInsightsLoadingSpinner" />
      ) : (
        renderResults()
      )}
    </div>
  );
}
